import { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { ChevronRight } from "lucide-react";
import { Sidebar } from "./components/Sidebar/Sidebar";

import { MealPlanProvider } from "./contexts/MealPlanContext";
import { ShoppingListProvider } from "./contexts/ShoppingListContext";
import { RemindersProvider, useReminders } from "./contexts/RemindersContext";
import { MealsProvider } from "./contexts/UserMealsContext";
import { UserSettingsProvider } from "./contexts/UserSettingsContext";
import { MealTagProvider } from "./contexts/MealTagContext";
import { IngredientUnitProvider } from "./contexts/IngredientUnitContext";
import { useAuth } from "./contexts/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { MealsTagsSync } from "./components/sync/MealsTagsSync";
import { getAlertCount } from "./utils/reminderUtils";

function AppShell() {
  const { user, logout } = useAuth();
  const { reminders } = useReminders();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const location = useLocation();

  return (
    <ErrorBoundary>
      <Toaster position="top-right" richColors />
      <div className="flex h-screen bg-[#fafafa] overflow-hidden relative">
        {/* Backdrop overlay for mobile */}
        {!isCollapsed && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setIsCollapsed(true)}
          />
        )}

        {/* Chevron button attached to sidebar edge - shows when sidebar is collapsed on mobile */}
        {isCollapsed && (
          <button
            onClick={() => setIsCollapsed(false)}
            className="fixed top-4 left-0 z-30 h-16 w-4 bg-white border-r border-t border-b border-gray-200 rounded-r-lg shadow-md hover:bg-gray-50 transition-colors md:hidden flex items-center justify-center"
            aria-label="Open menu"
          >
            <ChevronRight size={20} className="text-gray-700" />
          </button>
        )}

        <Sidebar
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          user={user}
          onLogout={logout}
          location={location}
          reminderAlertCount={getAlertCount(reminders)}
        />

        <main className="flex-1 overflow-auto w-full md:w-auto relative">
          <UserSettingsProvider>
            <MealTagProvider>
              <IngredientUnitProvider>
                <ShoppingListProvider>
                  <MealPlanProvider>
                    <MealsProvider>
                      <MealsTagsSync />
                      <Outlet />
                    </MealsProvider>
                  </MealPlanProvider>
                </ShoppingListProvider>
              </IngredientUnitProvider>
            </MealTagProvider>
          </UserSettingsProvider>
        </main>
      </div>
    </ErrorBoundary>
  );
}

export function App() {
  return (
    <RemindersProvider>
      <AppShell />
    </RemindersProvider>
  );
}
